<template>
    <!-- Identity: back control plus who this page is about. Always visible,
         because it replaces the page title row rather than hiding it. -->
    <div class="pl-dock-layer pl-dock-layer--identity">
        <div class="pl-dock">
            <!-- A route when there is one to go back to, a button when "back"
                 is a step inside this page rather than another page. -->
            <router-link
                v-if="backTo"
                class="pl-btn pl-btn--ghost pl-btn--sm"
                :to="backTo"
                :aria-label="backLabel"
            >
                <i class="fas fa-arrow-left" aria-hidden="true"></i>
            </router-link>
            <button
                v-else-if="backLabel"
                type="button"
                class="pl-btn pl-btn--ghost pl-btn--sm"
                :aria-label="backLabel"
                @click="$emit('back')"
            >
                <i class="fas fa-arrow-left" aria-hidden="true"></i>
            </button>

            <span class="pl-dock__ident">
                <span v-if="eyebrow" class="pl-eyebrow pl-truncate" :title="eyebrow">{{ eyebrow }}</span>
                <span class="pl-title pl-truncate" :title="title">{{ title }}</span>
            </span>
        </div>
    </div>

    <!-- Tools: always visible, top right. Anything that filters what is on
         screen belongs here rather than behind the handle. -->
    <div v-if="slots.tools" class="pl-dock-layer pl-dock-layer--tools">
        <div class="pl-dock">
            <slot name="tools"></slot>
        </div>
    </div>

    <!-- Actions: collapsed to a handle by default. The GM dock's arrangement,
         moved to the right so a game master sees both at once. -->
    <div v-if="slots.default" class="pl-dock-layer pl-dock-layer--actions">
        <transition name="pl-dock-fade">
            <div v-if="open" class="pl-dock pl-dock--menu" role="group" :aria-label="menuLabel">
                <slot></slot>
            </div>
        </transition>

        <button
            type="button"
            class="pl-dock__handle"
            :class="{ 'is-open': open }"
            :aria-expanded="open ? 'true' : 'false'"
            @click="open = !open"
        >
            <i class="fas" :class="open ? 'fa-chevron-down' : 'fa-chevron-up'" aria-hidden="true"></i>
            {{ menuLabel }}
        </button>
    </div>
</template>

<script setup>
import {onBeforeUnmount, onMounted, ref, useSlots} from 'vue';

defineProps({
    title: {type: String, default: ''},
    eyebrow: {type: String, default: ''},
    backTo: {type: [Object, String], default: null},
    backLabel: {type: String, default: ''},
    /* Only needed when the default slot is used — a dock with tools alone has
       no handle to label. */
    menuLabel: {type: String, default: ''},
});

defineEmits(['back']);

const slots = useSlots();

/* Open by default would put a panel back over the content, which is the thing
   this replaces. It starts closed and is one click away. */
const open = ref(false);

function onKeydown(event) {
    if (event.key === 'Escape') open.value = false;
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));
</script>
